import React from 'react';
import { SwapOptions } from './swapStateShared';
import { ExchangeEstimate } from '../../pages/SwapPage';
import { useSwap } from './useSwap';
import { useSwapV3 } from './useSwapV3';
import { useSwapMix } from '../swap_near_usdt';
import { usePersistSwapStore } from '../../stores/swapStore';
import { useDebounce } from 'react-use';
import Big from 'big.js';
import { toPrecision } from '../../utils/numbers';
import { APP_META } from '../../services/config';

export const useRefSwap = ({
  tokenIn,
  tokenInAmount,
  tokenOut,
  slippageTolerance,
  setLoadingData,
  loadingTrigger,
  setLoadingTrigger,
  setShowSwapLoading,
  loadingPause,
  swapMode,
  reEstimateTrigger,
  supportLedger,
  loadingData,
  tokenDeflationRateData,
}: SwapOptions): ExchangeEstimate => {
  const {
    canSwap,
    minAmountOut,
    swapError,
    makeSwap: makeSwapV1,
    avgFee: fee,
    swapsToDo,
    quoteDone,
    priceImpactValue,
    tokenInAmount: tokenInAmountV1,
    estimateInOut,
    swapsToDoServer,
    tag,
  } = useSwap({
    tokenIn,
    tokenInAmount,
    tokenOut,
    slippageTolerance,
    setLoadingData,
    loadingTrigger,
    setLoadingTrigger,
    setShowSwapLoading,
    loadingData,
    loadingPause,
    swapMode,
    reEstimateTrigger,
    supportLedger,
    tokenDeflationRateData,
  });
  const [estimateInAmount, tokenOutAmount] = estimateInOut;
  const {
    makeSwap: makeSwapV2,
    tokenOutAmount: tokenOutAmountV2,
    minAmountOut: minAmountOutV2,
    fee: feeV2,
    swapsToDoV2,
    priceImpact: priceImpactV2,
    quoteDone: quoteDoneV2,
    canSwap: canSwapV2,
    swapErrorV3: swapErrorV2,
    tokenInAmount: tokenInAmountV2,
    tag: tagV3,
  } = useSwapV3({
    tokenIn,
    tokenOut,
    tokenInAmount,
    slippageTolerance,
    swapMode,
    loadingTrigger,
    swapError,
    setLoadingTrigger,
    reEstimateTrigger,
  });
  const mixEstimateResult = useSwapMix({
    tokenIn,
    tokenInAmount,
    tokenOut,
    slippageTolerance,
    setLoadingData,
    loadingTrigger,
    setLoadingTrigger,
    setShowSwapLoading,
    loadingData,
    loadingPause,
    swapMode,
    reEstimateTrigger,
    supportLedger,
    tokenDeflationRateData,
  });
  const {
    estimateOutAmount: tokenOutAmountMix,
    mixTag,
    quoteDone: quoteDoneMix,
    canSwap: canSwapMix,
    fee: feeMix,
    priceImpact: priceImpactFix,
    estimateOutAmountWithSlippageTolerance: minAmountOutMix,
  } = mixEstimateResult;
  let bestSwap;
  const { set_near_usdt_swapTodos, set_near_usdt_swapTodos_transaction } =
    usePersistSwapStore();
  useDebounce(
    () => {
      if (bestSwap == 'mix') {
        set_near_usdt_swapTodos(mixEstimateResult);
      }
    },
    500,
    [bestSwap, JSON.stringify(mixEstimateResult || {})]
  );
  function validator() {
    if (tag && tagV3 && mixTag) {
      const [inId, outId, inAmount] = tag.split('|');
      const [dclInId, dclOutId, dclInAmount] = tagV3.split('|');
      const [mixInId, mixOutId, mixInAmount] = mixTag.split('|');
      return (
        inId == tokenIn?.id &&
        outId == tokenOut.id &&
        inAmount == tokenInAmount &&
        dclInId == tokenIn?.id &&
        dclOutId == tokenOut.id &&
        dclInAmount == tokenInAmount &&
        mixInId == tokenIn?.id &&
        mixOutId == tokenOut.id &&
        mixInAmount == tokenInAmount
      );
    }
    return false;
  }
  const tagParts = (tag || '').split('|');
  const clmmReady =
    quoteDone &&
    !swapError &&
    swapsToDo?.some((swapTodo) => !!(swapTodo as { clmm?: unknown }).clmm) &&
    tagParts[0] === tokenIn?.id &&
    tagParts[1] === tokenOut?.id &&
    tagParts[2] === tokenInAmount;
  const quoteDoneRef = quoteDoneV2 && quoteDone && quoteDoneMix && validator();
  if (clmmReady) {
    return {
      quoteDone: true,
      canSwap,
      makeSwap: makeSwapV1,
      estimates: swapsToDo?.map((swapTodo) => ({
        ...swapTodo,
        contract: (swapTodo as { clmm?: unknown }).clmm
          ? ('Ref_CLMM' as const)
          : ('Ref_Classic' as const),
      })),
      estimatesServer: swapsToDoServer ?? null,
      tokenOutAmount: toPrecision(
        tokenOutAmount || '0',
        Math.min(8, tokenOut?.decimals ?? 8)
      ),
      minAmountOut,
      fee,
      priceImpact: priceImpactValue,
      swapError,
      availableRoute: true,
      tokenInAmount: tokenInAmountV1,
      tokenIn,
      tokenOut,
      market: 'ref' as const,
      exchange_name: <div className="text-white">{APP_META.name}</div>,
    };
  }
  if (!quoteDoneRef)
    return {
      quoteDone: false,
      canSwap: false,
      tokenInAmount,
      tokenIn,
      tokenOut,
      market: 'ref',
      tokenOutAmount: '0',
    };
  // console.table([
  //   `tag---${tag}---${tokenOutAmount}`,
  //   `tagV3---${tagV3}---${tokenOutAmountV2}`,
  //   `mixTag---${mixTag}---${tokenOutAmountMix}`,
  // ]);
  const betterSwap =
    new Big(tokenOutAmountV2 || '0').gte(tokenOutAmount || '0') &&
    canSwapV2 &&
    !swapErrorV2
      ? 'v2'
      : 'v1';
  const betterOutAmount = new Big(tokenOutAmountV2 || '0').gte(
    tokenOutAmount || '0'
  )
    ? tokenOutAmountV2
    : tokenOutAmount;
  bestSwap =
    canSwapMix && Big(tokenOutAmountMix || '0').gt(betterOutAmount)
      ? 'mix'
      : betterSwap;
  // bestSwap = 'mix'; // TODO4 Test code
  if (bestSwap === 'v1') {
    if (swapsToDoServer) {
      swapsToDoServer.contract = 'Ref_Classic';
    }
    return {
      quoteDone: true,
      canSwap,
      makeSwap: makeSwapV1,
      estimates: swapsToDo?.map((swapTodo) => ({
        ...swapTodo,
        contract: 'Ref_Classic',
      })),
      estimatesServer: swapsToDoServer ?? null,
      tokenOutAmount:
        !tokenOutAmount || swapError
          ? ''
          : toPrecision(
              tokenOutAmount || '0',
              Math.min(8, tokenOut?.decimals ?? 8)
            ),
      minAmountOut,
      fee,
      priceImpact: priceImpactValue,
      swapError,
      availableRoute: !swapError,
      tokenInAmount: tokenInAmountV1,
      tokenIn,
      tokenOut,
      market: 'ref',
      exchange_name: <div className="text-white">{APP_META.name}</div>,
    };
  }
  if (bestSwap === 'v2') {
    return {
      quoteDone: true,
      canSwap: canSwapV2,
      makeSwap: makeSwapV2,
      estimates: swapsToDoV2?.map((swapTodo) => ({
        ...swapTodo,
        contract: 'Ref_DCL',
      })),

      tokenOutAmount:
        !tokenOutAmountV2 || swapErrorV2
          ? ''
          : toPrecision(
              tokenOutAmountV2 || '0',
              Math.min(8, tokenOut?.decimals ?? 8)
            ),
      tokenInAmount: tokenInAmountV2,
      minAmountOut: minAmountOutV2,
      fee: feeV2,
      priceImpact: priceImpactV2,
      swapError: swapErrorV2,
      availableRoute: !swapErrorV2,
      tokenIn,
      tokenOut,
      market: 'ref',
      exchange_name: <div className="text-white">{APP_META.name}</div>,
    };
  }
  if (bestSwap == 'mix') {
    return {
      estimatesMix: mixEstimateResult,
      quoteDone: true,
      canSwap: true,
      tokenOutAmount: mixEstimateResult.estimateOutAmount,
      minAmountOut: minAmountOutMix,
      tokenInAmount,
      availableRoute: !mixEstimateResult.mixError,
      tokenIn,
      tokenOut,
      market: 'ref',
      fee: feeMix,
      priceImpact: priceImpactFix,
      exchange_name: <div className="text-white">{APP_META.name}</div>,
    };
  }
};
